import { useState, useEffect, useRef } from "react";
import { useRemoteContent } from "@/lib/ContentContext";

const SLIDE_THEMES = [
  { dot: "bg-orange-400", glow: "rgba(251,146,60,0.12)", glowSolid: "rgba(251,146,60,0.8)" },
  { dot: "bg-amber-400", glow: "rgba(245,158,11,0.12)", glowSolid: "rgba(245,158,11,0.8)" },
  { dot: "bg-cyan-400", glow: "rgba(6,182,212,0.12)", glowSolid: "rgba(6,182,212,0.8)" },
  { dot: "bg-teal-400", glow: "rgba(45,212,191,0.12)", glowSolid: "rgba(45,212,191,0.8)" },
  { dot: "bg-rose-400", glow: "rgba(251,113,133,0.12)", glowSolid: "rgba(251,113,133,0.8)" },
  { dot: "bg-violet-400", glow: "rgba(167,139,250,0.12)", glowSolid: "rgba(167,139,250,0.8)" },
];

interface BannerProps {
  lang: "zh" | "en";
}

// ─── SlideBackground: renders video or image for a single slide ───────────
function SlideBackground({ slide, index, active }: { slide: { image: string; videoUrl?: string }; index: number; active: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Play/pause video when slide becomes active
  useEffect(() => {
    if (!videoRef.current) return;
    if (active) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [active]);

  const videoSrc = slide.videoUrl || null;

  return (
    <div
      className={`absolute inset-0 w-full h-full transition-all duration-700 ${
        active ? "opacity-100 scale-100" : "opacity-0 scale-105"
      }`}
      style={{ transitionTimingFunction: "cubic-bezier(0.4,0,0.2,1)" }}
    >
      {videoSrc ? (
        <video
          ref={videoRef}
          src={videoSrc}
          className="w-full h-full object-cover object-center"
          muted
          loop
          playsInline
          autoPlay={active}
        />
      ) : (
          <img
          src={slide.image}
           alt={`slide-${index}`}
           className="w-full h-full object-cover object-center"
         />
      )}
    </div>
  );
}

export default function Banner({ lang }: BannerProps) {
  const siteContent = useRemoteContent();
  const slides = siteContent.bannerSlides;
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const INTERVAL = 5500;

  // Reset current index if slides are removed
  useEffect(() => {
    if (current >= slides.length) {
      setCurrent(0);
    }
  }, [slides.length, current]);

  const goTo = (idx: number) => {
    if (transitioning || idx === current) return;
    setTransitioning(true);
    setProgress(0);
    setTimeout(() => {
      setCurrent(idx);
      setTransitioning(false);
    }, 500);
  };

  const next = () => goTo((current + 1) % slides.length);
  const prev = () => goTo((current - 1 + slides.length) % slides.length);

  const resetTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    timerRef.current = setInterval(next, INTERVAL);
    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + 100 / (INTERVAL / 50), 100));
    }, 50);
  };

  useEffect(() => {
    resetTimers();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [current, slides.length]);

  const safeIdx = Math.min(current, slides.length - 1);
  const theme = SLIDE_THEMES[safeIdx % SLIDE_THEMES.length];
  const contentSlide = slides[safeIdx];
  const text = lang === "zh"
    ? { title: contentSlide?.zhTitle ?? "", sub: contentSlide?.zhSub ?? "" }
    : { title: contentSlide?.enTitle ?? "", sub: contentSlide?.enSub ?? "" };

  return (
    <section id="banner" className="relative w-full h-screen min-h-[640px] overflow-hidden bg-xh-bg-deep">
      {/* Background slides */}
      {slides.map((s, i) => (
        <SlideBackground key={i} slide={s} index={i} active={i === safeIdx} />
      ))}

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />

      {/* Accent glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 60%, ${theme.glow}, transparent 70%)`,
          transition: "background 0.8s ease",
        }}
      />

      {/* Tech grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Corner decorative lines */}
      <div className="absolute top-24 left-8 hidden lg:block">
        <div className="w-12 h-[1px] bg-white/30" />
        <div className="w-[1px] h-12 bg-white/30" />
      </div>
      <div className="absolute top-24 right-8 hidden lg:block">
        <div className="w-12 h-[1px] bg-white/30 ml-auto" />
        <div className="w-[1px] h-12 bg-white/30 ml-auto" />
      </div>

      {/* Main content */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center text-center px-6 transition-all duration-500 ${
          transitioning ? "opacity-0 translate-y-6" : "opacity-100 translate-y-0"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.4,0,0.2,1)" }}
      >
        {/* Slide counter */}
        <div className="mb-5 flex items-center gap-3">
          <div className="h-[1px] w-10 bg-white/30" />
          <span className="text-white/40 text-xs font-mono tracking-widest">
            {String(safeIdx + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
          <div className="h-[1px] w-10 bg-white/30" />
        </div>

        {/* Badge */}
        <div className="mb-6 flex items-center gap-2 px-5 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md">
          <span className={`w-1.5 h-1.5 rounded-full ${theme.dot} animate-pulse`} />
          <span className="text-white/70 text-xs font-semibold tracking-[0.2em] uppercase">
            {lang === "zh" ? "星核动力科技" : "Aerospace Propulsion Technology"}
          </span>
        </div>

        {/* Main title */}
        <h1
          className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-none mb-5 tracking-tighter"
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "-0.02em" }}
        >
          {text.title}
        </h1>

        {/* Decorative line */}
        <div className="flex items-center gap-3 mb-6">
          <div
            className="h-[1px] w-16"
            style={{ background: `linear-gradient(to right, transparent, ${theme.glowSolid})` }}
          />
          <div className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} />
          <div
            className="h-[1px] w-16"
            style={{ background: `linear-gradient(to left, transparent, ${theme.glowSolid})` }}
          />
        </div>

        {/* Subtitle */}
        <p className="text-base md:text-lg text-white/60 max-w-xl mb-10 leading-relaxed tracking-wide">
          {text.sub}
        </p>
      </div>

      {/* Side navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => { prev(); resetTimers(); }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/8 hover:bg-white/18 border border-white/10 hover:border-white/25 backdrop-blur-sm text-white transition-all duration-300 cursor-pointer group"
          >
            <i className="ri-arrow-left-s-line text-xl group-hover:-translate-x-0.5 transition-transform duration-200" />
          </button>
          <button
            onClick={() => { next(); resetTimers(); }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/8 hover:bg-white/18 border border-white/10 hover:border-white/25 backdrop-blur-sm text-white transition-all duration-300 cursor-pointer group"
          >
            <i className="ri-arrow-right-s-line text-xl group-hover:translate-x-0.5 transition-transform duration-200" />
          </button>
        </>
      )}

      {/* Bottom dot nav */}
      <div className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-4">
        <div className="flex items-center gap-4">
          {slides.map((_, i) => {
            const t = SLIDE_THEMES[i % SLIDE_THEMES.length];
            return (
              <button
                key={i}
                onClick={() => { goTo(i); resetTimers(); }}
                className="relative flex items-center cursor-pointer"
              >
                <div
                  className={`h-[2px] transition-all duration-300 rounded-full overflow-hidden ${
                    i === safeIdx ? "w-16" : "w-6 opacity-40 hover:opacity-70"
                  } bg-white/25`}
                >
                  {i === safeIdx && (
                    <div
                      className={`h-full ${t.dot} transition-none`}
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-6 hidden md:flex flex-col items-center gap-2 text-white/30">
        <span className="text-[10px] tracking-[0.2em] uppercase" style={{ writingMode: "vertical-rl" }}>
          Scroll
        </span>
        <i className="ri-arrow-down-line animate-bounce text-xs" />
      </div>

      {/* Slide label */}
      <div className="absolute bottom-9 left-6 hidden md:block">
        <span className="text-white/25 text-xs tracking-widest font-mono uppercase">
          {lang === "zh"
            ? `第 ${safeIdx + 1} 张 / 共 ${slides.length} 张`
            : `Slide ${safeIdx + 1} of ${slides.length}`}
        </span>
      </div>
    </section>
  );
}
